import { Alert, Button, Checkbox, Select, Space, Table, Tooltip, Row, Col } from "antd";
import { CheckCircleFilled, DownOutlined, UpOutlined, FilterOutlined, CloudUploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import CardMarketing from "../../components/marketing/CardMarketing";
import DeploymentsImage from "../../assets/deployment-history.png";
import { useEffect, useState } from "react";
import { getElasticSearchService } from "../../backend";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { Dictionary, EnvDeployment, ExecutingTemplate, IEnvDetails } from "../../types";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import Heading from "../../components/Heading";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import { mapAppDeployments, mapInfraDeployments } from "../../utils/deployments";
import GitCommitLink from "../../components/GitCommitLink";
import { fetchProjectLimitations } from "../../utils/local-storage";
import MessageCard from "../../components/MessageCard";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import { handleUndeployEnvironment } from "../../utils/environment/handle-undeploy-environment";
import useReadLocalStorage from "../../contexts/LocalStorage";

export default function EnvironmentDeployments() {
    const { projectId, envId } = useParams();
    const executingTemplates = useReadLocalStorage<ExecutingTemplate[]>("executingTemplates");
    const currentProject = useRecoilValue(currentProjectState);
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const [loadingDeployments, setLoadingDeployments] = useState(true);
    const [deployments, setDeployments] = useState<EnvDeployment[]>([]);
    const [resourceNames, setResourceNames] = useState<string[]>([]);
    const [selectedResourceNames, setSelectedResourceNames] = useState<string[]>([]);
    const [onlyActive, setOnlyActive] = useState(false);
    const [numberOfExecutingTemplates, setNumberOfExecutingTemplates] = useState<number>(0);
    const limitations = fetchProjectLimitations();
    const navigate = useNavigate();
    const { open: openConfirmModal } = useDoubleConfigurationModal();

    useEffect(() => {
        const executingTemplatesLength = (executingTemplates || []).filter((template: any) => template.env.id === envId).length;
        setNumberOfExecutingTemplates(executingTemplatesLength);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [executingTemplates]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [
                    { data: { response: infraDeployments } },
                    { data: { response: appDeployments } }
                ] = await Promise.all([
                    getElasticSearchService().getStageDeploymentHistory(projectId!, envId!),
                    getElasticSearchService().getServiceDeploymentHistory(projectId!),
                ]);

                // All deployed apps across all clusters in the environment
                const allDeployedApps = envDetails.deployedMicroservices.flatMap(d => d.microservices);

                // Ignore deployments for the apps which are no longer deployed on the environment clusters
                const activeAppDeployments = appDeployments.filter(d => {
                    return allDeployedApps.find(a => a.name === d.name && a.kubernetesId === d.kubernetesId) !== undefined
                });

                // Combined infrastructure and app deployments
                const allDeployments = [
                    ...mapInfraDeployments(infraDeployments, envDetails.lastDeploymentId),
                    ...mapAppDeployments(activeAppDeployments, allDeployedApps.map(app => ({ ...app, microserviceId: app.id })))
                ].sort((d1, d2) => Number(new Date(d2.timestamp)) - Number(new Date(d1.timestamp)));

                // Unique resource/app names deployed in the environment
                const resourceNames = new Set(allDeployments.reduce((resources, deployment) => {
                    return [
                        ...resources,
                        ...deployment.resources.map(r => r.name)
                    ];
                }, [] as string[]));

                setDeployments(allDeployments);
                setResourceNames(Array.from(resourceNames));
            } catch (error) {
                console.log("ERROR", error);
            } finally {
                setLoadingDeployments(false);
            }
        }

        if (envDetails) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [envDetails.deployedMicroservices, envDetails.lastDeploymentId]);

    const isTrialEligible = (planId: string) => {
        return currentProject?.freeTrials && !(currentProject.freeTrials as Dictionary<boolean>)[planId];
    }

    const filterDeployments = () => {
        return deployments
            .filter(d => onlyActive ? d.active === true : true)
            .filter(d => {
                if (selectedResourceNames.length) {
                    return d.resources
                        .filter(r => !r.prev?.equal)
                        .find(r => selectedResourceNames.includes(r.name)) !== undefined
                } else {
                    return true;
                }
            });
    }

    return (
        <>
            {numberOfExecutingTemplates > 0 &&
                <Row style={{ marginTop: "-30px" }} align="top">
                    <Col xs={24}>
                        <MessageCard
                            type="info"
                            text={
                                <span>{`This environment has ${numberOfExecutingTemplates} recently executed template(s). `}<Link to={`/projects/${projectId}/environments/${envId}/active-templates`}>View details.</Link></span>
                            }
                        />
                    </Col>
                </Row>
            }

            <Heading
                title={<>
                    <CloudUploadOutlined /> Deployments
                </>}
                description="Overview of the infrastructure and application deployments for this environment."
                level={3}
            />
            <Space direction="vertical" size="large" className="full-width">
                {
                    envDetails.status === "ROLLBACK_FAILED" && envDetails.awsAccountId &&
                    <Alert
                        type="error"
                        message={
                            <Space size={5}>
                                The environment failed to rollback!
                                <Button
                                    type="link"
                                    className="no-padding"
                                    onClick={() => openConfirmModal({
                                        title: "Undeploy Infrastructure?",
                                        description: "The infrastructure resources in the environment will be permanently deleted from your cloud account.",
                                        confirmation: envId!,
                                        alert: <b>This action cannot be undone.</b>,
                                        cancelText: "Cancel",
                                        okText: "Undeploy Infrastructure",
                                        onOk: () => handleUndeployEnvironment({
                                            envId: envId!,
                                            projectId: projectId!,
                                            navigate
                                        })
                                    })}>
                                    Undeploy
                                </Button>
                                the environment and deploy the infrastructure again.
                            </Space>
                        }
                    />
                }
                {
                    envDetails.status === "UPDATE_ROLLBACK_FAILED" &&
                    <Alert
                        type="error"
                        message={
                            <>
                                The environment failed to rollback! To resolve this problem you need to <a href="https://aws.amazon.com/premiumsupport/knowledge-center/cloudformation-update-rollback-failed/" target="_blank" rel="noreferrer">continue the rollback process manually</a> from the AWS Console.
                            </>
                        }
                    />
                }

                <Table
                    pagination={false}
                    rowKey="id"
                    className="ant-table-wrapper-bordered"
                    loading={loadingDeployments}
                    dataSource={filterDeployments()}
                    expandable={{
                        expandIcon: ({ expanded, onExpand, record }) =>
                            !record.error && !record.status.errors?.length ? undefined :
                                expanded ?
                                    <UpOutlined onClick={e => onExpand(record, e)} /> :
                                    <DownOutlined onClick={e => onExpand(record, e)} />,
                        expandedRowRender: (record: any) => {
                            return (
                                record.error ?
                                    <div style={{ color: "red" }}>
                                        {record.error}
                                    </div> :
                                    <div style={{ margin: "-16px" }}>
                                        <Table
                                            showHeader={false}
                                            bordered={false}
                                            title={() =>
                                                <div className="flex-justify-space-between">
                                                    Deployment error logs
                                                </div>
                                            }
                                            dataSource={record.status.errors}
                                            columns={[
                                                {
                                                    title: "Resource ID",
                                                    render: event => <>
                                                        <div>
                                                            {event.logicalResourceId}
                                                        </div>
                                                        <div>
                                                            {event.resourceType}
                                                        </div>
                                                    </>
                                                },
                                                {
                                                    title: "Reason",
                                                    dataIndex: "statusReason"
                                                }
                                            ]}
                                            pagination={false}
                                            size="small"
                                        />
                                    </div>
                            )
                        }
                    }}
                    columns={[
                        {
                            title: "Deployment",
                            render: (deployment: EnvDeployment) => <Space size="middle">
                                <div>
                                    <Link to={
                                        deployment.type === "infrastructure" ?
                                            `/projects/${projectId}/environments/${envId}/deployments/infrastructure/${deployment.id}` :
                                            [
                                                "/projects",
                                                projectId,
                                                "environments",
                                                envId,
                                                "deployments",
                                                "apps",
                                                deployment.resources[0].kubernetesId,
                                                deployment.resources[0].kubernetesNamespace,
                                                deployment.resources[0].name,
                                                deployment.id
                                            ].join("/")
                                    }>
                                        <Space>
                                            {deployment.id}
                                            {
                                                deployment.active &&
                                                <Tooltip title="The deployment that is available to your end users.">
                                                    <CheckCircleFilled style={{
                                                        fontSize: 15,
                                                        color: "green"
                                                    }} />
                                                </Tooltip>
                                            }
                                        </Space>
                                    </Link>
                                    <div>
                                        <small>
                                            {deployment.type}
                                        </small>
                                    </div>
                                </div>
                            </Space>
                        },
                        {
                            title: 'Status',
                            key: 'status',
                            render: (deployment: EnvDeployment) => <DeploymentStatusTag status={deployment.status.name} />
                        },
                        {
                            title: 'Triggered by',
                            key: 'id',
                            render: (deployment: EnvDeployment) => <Space size="large">
                                <div>
                                    {deployment.createdBy}
                                    <div>
                                        <small>
                                            {deployment.createdAt}
                                        </small>
                                    </div>
                                </div>
                            </Space>
                        },
                        {
                            title: 'Updates',
                            width: "40%",
                            key: 'deployment',
                            render: (deployment: EnvDeployment) => deployment.resources
                                .filter(r => !r.prev?.equal)
                                .map(r => (
                                    <small key={r.name}>
                                        <div>
                                            <b>{r.name}</b> (<GitCommitLink version={r.version} repoUrl={r.repositoryUrl} commitType={r.commitType} branch={r.branch} hideIcon={true} />)
                                        </div>
                                        {
                                            deployment.type === "app" &&
                                            <div>
                                                Cluster: {r.kubernetesName}
                                                <br />
                                                Namespace: {r.kubernetesNamespace}
                                            </div>
                                        }
                                    </small>
                                ))
                        },
                        Table.EXPAND_COLUMN
                    ]}
                    title={() => (
                        <div className="flex-justify-space-between flex-align-center">
                            {
                                !limitations.deploymentInsights ?
                                    <div>
                                        Showing only last deployments. <Link to="/pricing/upgrade">Upgrade to Advanced Plan</Link> to enable deployment history.
                                    </div> :
                                    <Tooltip title="The deployments that are available to your end users.">
                                        <Checkbox onChange={(e) => setOnlyActive(e.target.checked)}>Only live deployments</Checkbox>
                                        <InfoCircleOutlined />
                                    </Tooltip>
                            }

                            <Select
                                mode="multiple"
                                showSearch={true}
                                showArrow={true}
                                allowClear={true}
                                placeholder="Filter by updated resources"
                                onChange={setSelectedResourceNames}
                                style={{ minWidth: '30%' }}
                                suffixIcon={<FilterOutlined />}
                            >
                                {
                                    resourceNames.map(name =>
                                        <Select.Option key={name}>{name}</Select.Option>
                                    )
                                }
                            </Select>
                        </div>
                    )}
                />
                {
                    !limitations.deploymentInsights &&
                    <>
                        <br />
                        <CardMarketing
                            title="Deployment History"
                            subtitle="Deployment history is available in Advanced Plan ($99/month)."
                            description="Know the state of your apps and infrastructure at any point in time. Follow deployments per environment in real-time, understand who did the changes and drill down to the exact commit."
                            action={
                                <Button>
                                    <Link to="/pricing/upgrade">
                                        {isTrialEligible("ADVANCED") ? "Start 7 day trial" : "Upgrade to Advanced Plan"}
                                    </Link>
                                </Button>
                            }
                            image={DeploymentsImage}
                            imageShadow={true}
                        />
                    </>
                }
            </Space >
        </>
    );
}